import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FormatPaintOutlinedIcon from '@mui/icons-material/FormatPaintOutlined';
import HorizontalSplitOutlinedIcon from '@mui/icons-material/HorizontalSplitOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import VerticalSplitOutlinedIcon from '@mui/icons-material/VerticalSplitOutlined';
import ViewSidebarOutlinedIcon from '@mui/icons-material/ViewSidebarOutlined';
import { Editor } from '@tinymce/tinymce-react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  GlobalStyles,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Editor as TinyMCEEditor } from 'tinymce';
import {
  strapiFetchWorkOrderLhuPdfHtml,
  strapiGetLaboratoryWorkOrderForm,
  strapiGetWorkOrderLhuCustomHtml,
  strapiSaveWorkOrderLhuCustomHtml,
  type LaboratoryResultBeritaAcaraEntry,
} from '../../../api/strapiCrm';
import { queryKeys } from '../../../query/queryKeys';
import { LhuStyleOverrideDialog, type LhuFormatTargets } from './LhuStyleOverrideDialog';
import {
  buildLhuStyleOverrideCss,
  countStyleOverrides,
  LHU_PAGE_FORMATS,
  lhuPageFormatOf,
  parseStoredStyles,
  readLhuStyleDefaults,
  sanitizeStyleOverrideMap,
  type LhuPageFormat,
  type LhuStyleOverrideMap,
} from './lhuStyleOverrides';
import 'tinymce/tinymce';
import 'tinymce/models/dom/model';
import 'tinymce/themes/silver';
import 'tinymce/icons/default';
import 'tinymce/plugins/advlist';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/table';
import 'tinymce/plugins/pagebreak';
import 'tinymce/skins/ui/oxide/skin.min.css';

/** Applied after the LHU stylesheet so absolutely positioned page parts stay editable. */
const EDITOR_CONTENT_STYLE = `
  html { background: #fff; }
  body {
    margin: 0;
    padding: 16px 20px 32px;
    background: #fff;
    font-family: Arial, Helvetica, sans-serif;
    color: #111;
    line-height: 1.35;
  }
  body.lhu-editor-cover .cover-banner {
    position: static;
    display: block;
    padding: 8px 10px;
    margin-bottom: 12px;
    background: #37474f;
  }
  body.lhu-editor-cover .cover-banner-text { display: block; transform: none; text-align: left; }
  body.lhu-editor-cover .cover-banner-text p { writing-mode: horizontal-tb; white-space: normal; }
  body.lhu-editor-cover .cover-body { height: auto; padding: 0; display: block; }
  .attach-photo { height: 28mm; }
  .page-break { border-top: 1px dashed #90a4ae; }
  html { height: 100%; cursor: text; }
  body { min-height: 100%; cursor: text; }
  p.lhu-plain {
    margin: 0 0 2px;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 12px;
    font-weight: 400;
    font-style: normal;
    letter-spacing: normal;
    text-align: left;
    text-decoration: none;
    color: #111;
    line-height: 1.35;
  }
`;

const PREVIEW_HOST_STYLE = `
  :host { display: block; background: #d9d9d9; min-height: 100%; }
  .lhu-preview-pages {
    background: #d9d9d9;
    padding: 8px 0 24px;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 10px;
    line-height: 1.35;
    color: #111;
  }
  .page.lhu-preview-active { box-shadow: 0 0 0 2px #1976d2, 0 2px 8px rgba(0,0,0,.18); }
`;

type LhuPreviewPage = {
  slot: string;
  label: string;
  /** Page body HTML as rendered by the backend (generated data or saved edit). */
  body: string;
};

function parseStoredSlots(raw: string): Record<string, string> {
  const text = raw.trim();
  if (!text) return {};
  if (text.startsWith('{')) {
    try {
      const parsed = JSON.parse(text) as { slots?: unknown };
      if (!parsed.slots || typeof parsed.slots !== 'object' || Array.isArray(parsed.slots)) return {};
      const slots: Record<string, string> = {};
      for (const [key, value] of Object.entries(parsed.slots as Record<string, unknown>)) {
        if (typeof value === 'string') slots[key] = value;
      }
      return slots;
    } catch {
      return {};
    }
  }
  return { letter: text };
}

function kvValue(page: Element, label: string): string {
  for (const row of page.querySelectorAll('.kv')) {
    if (row.querySelector('.k')?.textContent?.trim() !== label) continue;
    const spans = row.querySelectorAll('span');
    return spans[spans.length - 1]?.textContent?.trim() ?? '';
  }
  return '';
}

function isBiologyLhuGroup(group: string | null | undefined): boolean {
  return group === 'plankton' || group === 'bentos';
}

function sampleLabels(entry: LaboratoryResultBeritaAcaraEntry): string[] {
  return [entry.point_label, entry.row_label].map((label) => label.trim()).filter(Boolean);
}

/** Secondary plankton/bentos sudah ada di halaman hasil uji, jadi halamannya tidak ditampilkan lagi. */
function isBiologySecondaryPage(page: Element, biologySampleIds: Set<string>): boolean {
  const sampleId = kvValue(page, 'Laboratory Sample ID');
  if (sampleId && biologySampleIds.has(sampleId)) return true;
  const heading = page.querySelector('h2.section')?.textContent?.trim().toUpperCase() ?? '';
  return heading.includes('PLANKTON') || heading.includes('BENTOS');
}

/**
 * Sisipkan halaman secondary data setelah summary sampel yang sama, bila preview backend belum memuatnya.
 * Halaman secondary plankton dan bentos dibuang karena isinya sudah ada di halaman hasil uji.
 */
function insertSecondaryDataPages(
  html: string,
  entries: LaboratoryResultBeritaAcaraEntry[],
): string {
  if (!html.trim()) return html;
  const biologySampleIds = new Set<string>();
  const bySampleId = new Map<string, string>();
  for (const entry of entries) {
    const labels = sampleLabels(entry);
    if (isBiologyLhuGroup(entry.lhu_grouped_name)) {
      for (const key of labels) biologySampleIds.add(key);
      continue;
    }
    const content = (entry.secondary_data_html ?? '').trim();
    if (!content) continue;
    for (const key of labels) bySampleId.set(key, content);
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  let changed = false;
  for (const page of Array.from(doc.querySelectorAll('section.page'))) {
    const slot = page.getAttribute('data-lhu-slot') ?? '';
    if (!slot.startsWith('secondary:')) continue;
    if (!isBiologySecondaryPage(page, biologySampleIds)) continue;
    page.remove();
    changed = true;
  }

  const hasSecondary = doc.querySelector('section.page[data-lhu-slot^="secondary:"]') != null;
  if (!hasSecondary && bySampleId.size > 0) {
    const resultPages = Array.from(doc.querySelectorAll('section.page')).filter((page) =>
      (page.getAttribute('data-lhu-slot') ?? '').startsWith('result:'),
    );
    for (const page of resultPages) {
      const sampleId = kvValue(page, 'Laboratory Sample ID');
      const content = bySampleId.get(sampleId);
      if (!content) continue;
      const resultSlot = page.getAttribute('data-lhu-slot') ?? 'result:sample';
      const section = doc.createElement('section');
      section.className = 'page';
      section.setAttribute('data-lhu-slot', `secondary:${resultSlot.slice('result:'.length)}`);
      const watermark = page.querySelector(':scope > .watermark');
      if (watermark) section.appendChild(watermark.cloneNode(true));
      const body = doc.createElement('div');
      body.className = 'page-inner lhu-page-body';
      body.innerHTML = prepareCustomFragment(content) || content;
      section.appendChild(body);
      const foot = page.querySelector(':scope > .doc-foot');
      if (foot) section.appendChild(foot.cloneNode(true));
      page.after(section);
      changed = true;
    }
  }

  if (!changed) return html;
  return `<!DOCTYPE html>\n${doc.documentElement.outerHTML}`;
}

function pageLabel(page: Element, index: number): string {
  const n = index + 1;
  const slot = page.getAttribute('data-lhu-slot') ?? '';
  if (slot === 'cover') return `Halaman ${n} · Cover`;
  if (slot === 'letter') return `Halaman ${n} · Surat`;
  if (slot === 'sample-info') return `Halaman ${n} · Informasi sampel`;
  if (slot.startsWith('attachment:')) return `Halaman ${n} · Lampiran`;
  if (slot.startsWith('result:')) {
    const id = kvValue(page, 'Laboratory Sample ID');
    return id ? `Halaman ${n} · Hasil uji · ${id}` : `Halaman ${n} · Hasil uji`;
  }
  if (slot.startsWith('secondary:')) {
    const heading = page.querySelector('h2.section')?.textContent?.trim() ?? '';
    return heading ? `Halaman ${n} · ${heading}` : `Halaman ${n} · Secondary data`;
  }
  const heading = page.querySelector('h2.section')?.textContent?.trim() ?? '';
  return heading ? `Halaman ${n} · ${heading}` : `Halaman ${n}`;
}

function parseLhuPreview(html: string): { pages: LhuPreviewPage[]; css: string } {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const pages = Array.from(doc.querySelectorAll('section.page')).map((page, index) => ({
    slot: page.getAttribute('data-lhu-slot') || `page-${index + 1}`,
    label: pageLabel(page, index),
    body: page.querySelector('.lhu-page-body')?.innerHTML.trim() ?? '',
  }));
  const css = Array.from(doc.querySelectorAll('style:not([data-lhu-style-overrides])'))
    .map((node) => node.textContent ?? '')
    .join('\n');
  return { pages, css };
}

function prepareCustomFragment(html: string): string {
  const trimmed = html.trim();
  if (!trimmed) return '';
  const doc = new DOMParser().parseFromString(`<div>${trimmed}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return '';
  root.querySelectorAll('script,iframe,object,embed,link,meta,style').forEach((el) => el.remove());
  root.querySelectorAll('*').forEach((el) => {
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      if (name.startsWith('on') || name === 'srcdoc') el.removeAttribute(attr.name);
    }
  });
  return root.innerHTML;
}

/** Sample photos are stored without src; the backend fills it from the sample when rendering. */
function storablePageBody(html: string): string {
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return '<p>&nbsp;</p>';
  root.querySelectorAll('img[data-lhu-photo]').forEach((img) => img.removeAttribute('src'));
  return root.innerHTML.trim() || '<p>&nbsp;</p>';
}

const PLAIN_LINE_CLASS = 'lhu-plain';

function isPlainParagraph(node: Element | null): node is HTMLParagraphElement {
  if (!node || node.tagName !== 'P' || node.getAttribute('style')) return false;
  const classes = (node.getAttribute('class') ?? '').split(/\s+/).filter(Boolean);
  return classes.every((name) => name === PLAIN_LINE_CLASS);
}

function plainParagraph(editor: TinyMCEEditor, text: string): HTMLParagraphElement {
  const doc = editor.getDoc();
  const p = doc.createElement('p');
  p.className = PLAIN_LINE_CLASS;
  if (text.replace(/\u00a0/g, ' ').trim()) p.textContent = text;
  else p.appendChild(doc.createElement('br'));
  return p;
}

function blockInContainer(editor: TinyMCEEditor, container: HTMLElement): HTMLElement | null {
  const start = editor.selection.getStart();
  if (!start || !container.contains(start)) return null;
  const block = editor.dom.getParent(start, (node) => (node as HTMLElement).parentElement === container, container);
  return block && block !== container ? (block as HTMLElement) : null;
}

function plainLineAnchor(editor: TinyMCEEditor): { container: HTMLElement; anchor: Element | null } {
  const body = editor.getBody();
  const start = editor.selection.getStart();
  const cell =
    start && body.contains(start) ? (editor.dom.getParent(start, 'td,th', body) as HTMLElement | null) : null;
  const container = cell ?? body;
  return { container, anchor: blockInContainer(editor, container) ?? container.lastElementChild };
}

const COPIED_TEXT_PROPS = [
  'font-family',
  'font-size',
  'font-weight',
  'font-style',
  'color',
  'letter-spacing',
  'text-decoration-line',
] as const;

function caretTextStyles(editor: TinyMCEEditor): Record<string, string> {
  const start = editor.selection.getStart();
  const styles: Record<string, string> = { margin: '0 0 2px' };
  if (!start) return styles;
  const view = editor.getWin();
  const computed = view.getComputedStyle(start);
  for (const prop of COPIED_TEXT_PROPS) {
    const value = computed.getPropertyValue(prop).trim();
    if (!value || value === 'normal' || value === 'none') continue;
    styles[prop] = value;
  }
  const body = editor.getBody();
  const alignSource =
    (editor.dom.getParent(start, 'td,th', body) as Element | null) ??
    (editor.dom.getParent(start, (node) => node !== body && editor.dom.isBlock(node), body) as Element | null) ??
    start;
  const align = view.getComputedStyle(alignSource).textAlign.trim();
  if (align) styles['text-align'] = align;
  return styles;
}

/** New line after the current block, using the font and alignment at the caret. */
function insertFormattedLine(editor: TinyMCEEditor) {
  const styles = caretTextStyles(editor);
  const { container, anchor } = plainLineAnchor(editor);
  editor.undoManager.transact(() => {
    const p = editor.getDoc().createElement('p');
    for (const [name, value] of Object.entries(styles)) p.style.setProperty(name, value);
    p.appendChild(editor.getDoc().createElement('br'));
    if (anchor && anchor.parentElement === container) editor.dom.insertAfter(p, anchor);
    else container.appendChild(p);
    editor.selection.setCursorLocation(p, 0);
  });
  editor.focus();
  editor.nodeChanged();
}

/** Puts the caret in a paragraph that has no inline style and no template class. */
function insertPlainLine(editor: TinyMCEEditor) {
  const { container, anchor } = plainLineAnchor(editor);
  editor.undoManager.transact(() => {
    const p = plainParagraph(editor, '');
    if (anchor && anchor.parentElement === container) editor.dom.insertAfter(p, anchor);
    else container.appendChild(p);
    editor.selection.setCursorLocation(p, 0);
  });
  editor.focus();
  editor.nodeChanged();
}

function plainLineFromNewBlock(editor: TinyMCEEditor, block: Element) {
  if (block.tagName === 'LI' || block.closest('li')) return;
  if (isPlainParagraph(block)) {
    const extra = Array.from(block.children).some((child) => child.tagName !== 'BR');
    if (!extra && !block.getAttribute('style')) {
      block.classList.add(PLAIN_LINE_CLASS);
      return;
    }
  }
  const p = plainParagraph(editor, block.textContent ?? '');
  block.replaceWith(p);
  editor.selection.setCursorLocation(p, 0);
}

function focusPlainLineBelow(editor: TinyMCEEditor, clientY: number) {
  const body = editor.getBody();
  const last = body.lastElementChild;
  if (last) {
    const bottom = last.getBoundingClientRect().bottom;
    if (clientY <= bottom + 8) return;
    if (isPlainParagraph(last) && editor.dom.isEmpty(last)) {
      editor.selection.setCursorLocation(last, 0);
      editor.focus();
      return;
    }
  }
  editor.undoManager.transact(() => {
    const p = plainParagraph(editor, '');
    body.appendChild(p);
    editor.selection.setCursorLocation(p, 0);
  });
  editor.focus();
  editor.nodeChanged();
}

function bindPlainCaret(editor: TinyMCEEditor) {
  editor.ui.registry.addButton('lhuPlainLine', {
    icon: 'plus',
    text: 'Baris polos',
    tooltip: 'Taruh kursor di baris baru tanpa format',
    onAction: () => insertPlainLine(editor),
  });
  editor.ui.registry.addButton('lhuFormattedLine', {
    icon: 'duplicate',
    text: 'Baris baru dengan formatter',
    tooltip: 'Baris baru dengan format yang sama seperti teks di kursor',
    onAction: () => insertFormattedLine(editor),
  });
  editor.on('keydown', (event) => {
    if (event.key !== 'Enter' || event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.isComposing || !editor.selection.isCollapsed()) return;
    const start = editor.selection.getStart();
    const body = editor.getBody();
    if (!start || editor.dom.getContentEditable(start) === 'false') return;
    if (editor.dom.getParent(start, 'li', body)) return;
    const { container } = plainLineAnchor(editor);
    const block = blockInContainer(editor, container);
    if (block && isPlainParagraph(block)) return;
    event.preventDefault();
    insertPlainLine(editor);
  });
  editor.on('NewBlock', (event) => {
    plainLineFromNewBlock(editor, event.newBlock);
  });
  editor.on('click', (event) => {
    if (event.target !== editor.getBody()) return;
    focusPlainLineBelow(editor, event.clientY);
  });
}

const LIVE_OVERRIDES_ATTR = 'data-lhu-live-overrides';

function liveOverridesStyle(host: HTMLDivElement | null): HTMLStyleElement | null {
  return host?.shadowRoot?.querySelector<HTMLStyleElement>(`style[${LIVE_OVERRIDES_ATTR}]`) ?? null;
}

function mountLhuPreview(host: HTMLDivElement, html: string) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const shadow = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
  shadow.replaceChildren();
  const style = document.createElement('style');
  style.textContent = `${Array.from(doc.querySelectorAll('style:not([data-lhu-style-overrides])'))
    .map((node) => node.textContent ?? '')
    .join('\n')}\n${PREVIEW_HOST_STYLE}`;
  shadow.appendChild(style);
  const liveOverrides = document.createElement('style');
  liveOverrides.setAttribute(LIVE_OVERRIDES_ATTR, '');
  shadow.appendChild(liveOverrides);
  const pages = document.createElement('div');
  pages.className = 'lhu-preview-pages';
  Array.from(doc.body.childNodes).forEach((node) => {
    pages.appendChild(node.cloneNode(true));
  });
  shadow.appendChild(pages);
}

function pageSelector(slot: string): string {
  return `.page[data-lhu-slot="${CSS.escape(slot)}"]`;
}

/**
 * Shows unsaved edits in the preview. Slots in `applied` that are no longer edited get their
 * fetched body back.
 */
function applyLhuPageBodies(
  host: HTMLDivElement | null,
  slots: Record<string, string>,
  pages: LhuPreviewPage[],
  applied: Set<string>,
): Set<string> {
  const root = host?.shadowRoot;
  if (!root) return applied;
  const fetched = new Map(pages.map((page) => [page.slot, page.body]));
  for (const slot of new Set([...applied, ...Object.keys(slots)])) {
    const html = slots[slot] ?? fetched.get(slot);
    const target = root.querySelector<HTMLElement>(`${pageSelector(slot)} .lhu-page-body`);
    if (html === undefined || !target) continue;
    const next = prepareCustomFragment(html);
    if (target.innerHTML !== next) target.innerHTML = next;
  }
  return new Set(Object.keys(slots));
}

type Props = {
  open: boolean;
  workOrderDocumentId: string;
  jwt: string | null;
  onClose: () => void;
};

type LhuPanelMode = 'left' | 'both' | 'right';

function LhuPanelModeToggle({
  mode,
  vertical,
  onChange,
}: {
  mode: LhuPanelMode;
  vertical: boolean;
  onChange: (mode: LhuPanelMode) => void;
}) {
  const tip = vertical ? 'left' : 'bottom';
  const buttonSx = {
    px: 0.75,
    py: 0.75,
    textTransform: 'none',
    lineHeight: 1.1,
  };
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      orientation={vertical ? 'vertical' : 'horizontal'}
      value={mode}
      onChange={(_, value: LhuPanelMode | null) => {
        if (value) onChange(value);
      }}
      aria-label="Tampilan panel"
      data-testid="lhu-panel-mode"
    >
      <ToggleButton value="left" aria-label="Tampilkan panel kiri" data-testid="lhu-panel-mode-left" sx={buttonSx}>
        <Tooltip title="Hanya konten" placement={tip}>
          <Stack component="span" alignItems="center" spacing={0.25}>
            <ViewSidebarOutlinedIcon fontSize="small" />
            <Typography component="span" variant="caption" sx={{ fontSize: 10, lineHeight: 1 }}>
              Kiri
            </Typography>
          </Stack>
        </Tooltip>
      </ToggleButton>
      <ToggleButton value="both" aria-label="Tampilkan kedua panel" data-testid="lhu-panel-mode-both" sx={buttonSx}>
        <Tooltip title="Konten dan preview" placement={tip}>
          <Stack component="span" alignItems="center" spacing={0.25}>
            {vertical ? (
              <VerticalSplitOutlinedIcon fontSize="small" />
            ) : (
              <HorizontalSplitOutlinedIcon fontSize="small" />
            )}
            <Typography component="span" variant="caption" sx={{ fontSize: 10, lineHeight: 1 }}>
              Kedua
            </Typography>
          </Stack>
        </Tooltip>
      </ToggleButton>
      <ToggleButton value="right" aria-label="Tampilkan panel kanan" data-testid="lhu-panel-mode-right" sx={buttonSx}>
        <Tooltip title="Hanya preview" placement={tip}>
          <Stack component="span" alignItems="center" spacing={0.25}>
            <ViewSidebarOutlinedIcon fontSize="small" sx={{ transform: 'scaleX(-1)' }} />
            <Typography component="span" variant="caption" sx={{ fontSize: 10, lineHeight: 1 }}>
              Kanan
            </Typography>
          </Stack>
        </Tooltip>
      </ToggleButton>
    </ToggleButtonGroup>
  );
}

export function LhuCustomHtmlDialog({
  open,
  workOrderDocumentId,
  jwt,
  onClose,
}: Props) {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const previewHostRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<TinyMCEEditor | null>(null);
  const activeSlotRef = useRef<string | null>(null);
  /** Unsaved page edits from this session, keyed by slot. */
  const slotsRef = useRef<Record<string, string>>({});
  const storedSlotKeysRef = useRef<string[]>([]);
  const pagesRef = useRef<LhuPreviewPage[]>([]);
  const appliedSlotsRef = useRef<Set<string>>(new Set());
  const [slots, setSlots] = useState<Record<string, string>>({});
  const [storedSlotKeys, setStoredSlotKeys] = useState<string[]>([]);
  const [slotsReady, setSlotsReady] = useState(false);
  const [activeSlot, setActiveSlot] = useState<string | null>(null);
  const [editorNonce, setEditorNonce] = useState(0);
  const [editorSeed, setEditorSeed] = useState<{ slot: string; html: string; nonce: number } | null>(null);
  const [previewMounted, setPreviewMounted] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveOk, setSaveOk] = useState(false);
  const [panelMode, setPanelMode] = useState<LhuPanelMode>('both');
  const [styles, setStyles] = useState<LhuStyleOverrideMap>({});
  const [styleDialog, setStyleDialog] = useState<{
    initialFormat: LhuPageFormat;
    formats: Record<LhuPageFormat, LhuFormatTargets>;
  } | null>(null);
  const stylesRef = useRef<LhuStyleOverrideMap>({});
  const slotsReadyRef = useRef(false);
  activeSlotRef.current = activeSlot;
  slotsRef.current = slots;
  storedSlotKeysRef.current = storedSlotKeys;
  stylesRef.current = styles;
  slotsReadyRef.current = slotsReady;

  const htmlQ = useQuery({
    queryKey: queryKeys.lhuCustomHtml(jwt, workOrderDocumentId),
    queryFn: () => strapiGetWorkOrderLhuCustomHtml(jwt!, workOrderDocumentId),
    enabled: open && Boolean(jwt) && Boolean(workOrderDocumentId),
  });

  const previewQ = useQuery({
    queryKey: queryKeys.lhuPdfHtmlPreview(jwt, workOrderDocumentId),
    queryFn: () => strapiFetchWorkOrderLhuPdfHtml(jwt!, workOrderDocumentId),
    enabled: open && Boolean(jwt) && Boolean(workOrderDocumentId),
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const formQ = useQuery({
    queryKey: queryKeys.laboratoryResultWorkOrderForm(jwt, workOrderDocumentId),
    queryFn: () => strapiGetLaboratoryWorkOrderForm(jwt!, workOrderDocumentId),
    enabled: open && Boolean(jwt) && Boolean(workOrderDocumentId),
  });

  const previewSource = useMemo(
    () => insertSecondaryDataPages(previewQ.data ?? '', formQ.data?.berita_acara ?? []),
    [previewQ.data, formQ.data?.berita_acara],
  );

  const preview = useMemo(
    () => (previewSource ? parseLhuPreview(previewSource) : { pages: [], css: '' }),
    [previewSource],
  );
  const pages = preview.pages;
  pagesRef.current = pages;

  useEffect(() => {
    if (!open) {
      setSlots({});
      setStoredSlotKeys([]);
      setSlotsReady(false);
      setActiveSlot(null);
      setEditorNonce(0);
      setEditorSeed(null);
      setPreviewMounted(false);
      setSaveError(null);
      setSaveOk(false);
      setStyles({});
      setStyleDialog(null);
      return;
    }
    if (htmlQ.isSuccess && !slotsReady) {
      setStoredSlotKeys(Object.keys(parseStoredSlots(htmlQ.data)));
      setStyles(parseStoredStyles(htmlQ.data));
      setSlotsReady(true);
    }
  }, [open, htmlQ.isSuccess, htmlQ.data, slotsReady]);

  useEffect(() => {
    if (!open || pages.length === 0) return;
    setActiveSlot((current) =>
      current && pages.some((page) => page.slot === current) ? current : pages[0].slot,
    );
  }, [open, pages]);

  useEffect(() => {
    if (!open || !slotsReady || !activeSlot) return;
    const page = pagesRef.current.find((p) => p.slot === activeSlot);
    if (!page) return;
    setEditorSeed((current) => {
      if (current && current.slot === activeSlot && current.nonce === editorNonce) return current;
      return { slot: activeSlot, html: slotsRef.current[activeSlot] ?? page.body, nonce: editorNonce };
    });
  }, [open, slotsReady, activeSlot, editorNonce, pages]);

  const paintPreview = useCallback((host: HTMLDivElement | null, html: string | undefined) => {
    previewHostRef.current = host;
    if (!open || !host || !html) return;
    mountLhuPreview(host, html);
    appliedSlotsRef.current = new Set();
    if (slotsReadyRef.current) {
      appliedSlotsRef.current = applyLhuPageBodies(host, slotsRef.current, pagesRef.current, new Set());
      const live = liveOverridesStyle(host);
      if (live) live.textContent = buildLhuStyleOverrideCss(stylesRef.current);
    }
    const slot = activeSlotRef.current;
    if (slot) host.shadowRoot?.querySelector(pageSelector(slot))?.classList.add('lhu-preview-active');
    setPreviewMounted(true);
  }, [open]);

  const previewRef = useCallback(
    (node: HTMLDivElement | null) => paintPreview(node, previewSource),
    [paintPreview, previewSource],
  );

  useEffect(() => {
    paintPreview(previewHostRef.current, previewSource);
    return () => {
      previewHostRef.current?.shadowRoot?.replaceChildren();
      setPreviewMounted(false);
    };
  }, [paintPreview, previewSource]);

  useEffect(() => {
    if (!previewMounted || !slotsReady) return;
    appliedSlotsRef.current = applyLhuPageBodies(
      previewHostRef.current,
      slots,
      pagesRef.current,
      appliedSlotsRef.current,
    );
  }, [previewMounted, slotsReady, slots]);

  useEffect(() => {
    if (!previewMounted || !slotsReady) return;
    const el = liveOverridesStyle(previewHostRef.current);
    if (el) el.textContent = buildLhuStyleOverrideCss(styles);
  }, [previewMounted, slotsReady, styles]);

  function recordEdit(slot: string, html: string) {
    setSaveOk((ok) => (ok ? false : ok));
    setSlots((prev) => (prev[slot] === html ? prev : { ...prev, [slot]: html }));
  }

  function openStyleDialog() {
    const root = previewHostRef.current?.shadowRoot;
    if (!root) return;
    const live = liveOverridesStyle(previewHostRef.current);
    if (live) live.disabled = true;
    const formats = Object.fromEntries(
      LHU_PAGE_FORMATS.map((f) => {
        const formatPages = Array.from(root.querySelectorAll(f.selector));
        return [f.key, { targets: readLhuStyleDefaults(formatPages), pageCount: formatPages.length }];
      }),
    ) as Record<LhuPageFormat, LhuFormatTargets>;
    if (live) live.disabled = false;
    const activeFormat = activeSlotRef.current ? lhuPageFormatOf(activeSlotRef.current) : null;
    setStyleDialog({ initialFormat: activeFormat ?? 'cover', formats });
  }

  function applyStyles(next: LhuStyleOverrideMap) {
    setStyles(next);
    setSaveOk(false);
    setStyleDialog(null);
  }

  const totalOverrideCount = countStyleOverrides(styles);

  useEffect(() => {
    if (panelMode === 'right') return;
    const timer = window.setTimeout(() => {
      const editor = editorRef.current;
      if (!editor || editor.removed) return;
      const container = editor.getContainer();
      container.style.width = '100%';
      container.style.height = '100%';
      editor.dispatch('ResizeWindow');
      editor.dispatch('ResizeEditor');
    }, 0);
    return () => window.clearTimeout(timer);
  }, [panelMode]);

  useEffect(() => {
    const host = previewHostRef.current;
    const root = host?.shadowRoot;
    if (!host || !root || !previewMounted || !activeSlot) return;
    root.querySelectorAll('.page.lhu-preview-active').forEach((el) => {
      el.classList.remove('lhu-preview-active');
    });
    const page = root.querySelector(pageSelector(activeSlot));
    if (!(page instanceof HTMLElement)) return;
    page.classList.add('lhu-preview-active');
    const hostTop = host.getBoundingClientRect().top;
    const pageTop = page.getBoundingClientRect().top;
    host.scrollTop += pageTop - hostTop;
  }, [activeSlot, previewMounted]);

  function selectPage(slot: string) {
    if (slot === activeSlotRef.current) return;
    setSaveOk(false);
    setActiveSlot(slot);
  }

  const saveMut = useMutation({
    mutationFn: (opts: { resetSlot?: string }) => {
      const fetched = new Map(pagesRef.current.map((page) => [page.slot, page.body]));
      const keys = new Set([...storedSlotKeysRef.current, ...Object.keys(slotsRef.current)]);
      if (opts.resetSlot) keys.delete(opts.resetSlot);
      const clean: Record<string, string> = {};
      for (const key of keys) {
        const html = slotsRef.current[key] ?? fetched.get(key);
        if (html === undefined) continue;
        clean[key] = storablePageBody(html);
      }
      const cleanStyles = sanitizeStyleOverrideMap(stylesRef.current);
      const payload = {
        v: 2,
        slots: clean,
        ...(Object.keys(cleanStyles).length ? { styles: cleanStyles } : {}),
      };
      return strapiSaveWorkOrderLhuCustomHtml(jwt!, workOrderDocumentId, JSON.stringify(payload));
    },
    onSuccess: async (stored) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.lhuCustomHtml(jwt, workOrderDocumentId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.lhuPdfHtmlPreview(jwt, workOrderDocumentId) }),
      ]);
      setSaveError(null);
      setSaveOk(true);
      setSlots({});
      setStoredSlotKeys(Object.keys(parseStoredSlots(stored)));
      setStyles(parseStoredStyles(stored));
      setEditorNonce((n) => n + 1);
    },
    onError: (err) => {
      setSaveOk(false);
      setSaveError(err instanceof Error ? err.message : 'Gagal menyimpan konten LHU.');
    },
  });

  const editedSlots = new Set([...storedSlotKeys, ...Object.keys(slots)]);
  const activeEdited = activeSlot ? editedSlots.has(activeSlot) : false;

  function resetActivePage() {
    const slot = activeSlotRef.current;
    if (!slot) return;
    if (!storedSlotKeysRef.current.includes(slot)) {
      setSlots((prev) => {
        const next = { ...prev };
        delete next[slot];
        return next;
      });
      setEditorNonce((n) => n + 1);
      return;
    }
    const ok = window.confirm(
      'Kembalikan halaman ini ke data asli? Editan halaman ini dihapus dan perubahan lain langsung disimpan.',
    );
    if (ok) saveMut.mutate({ resetSlot: slot });
  }

  const loadingEditor = !htmlQ.isError && !slotsReady;
  const showEditor =
    slotsReady &&
    editorSeed !== null &&
    editorSeed.slot === activeSlot &&
    editorSeed.nonce === editorNonce &&
    !htmlQ.isError;
  const editorContentStyle = `${preview.css}\n${EDITOR_CONTENT_STYLE}`;

  return (
    <Box
      data-testid="lhu-custom-html-page"
      sx={{ flex: 1, minHeight: 0, height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <GlobalStyles
        styles={{
          '.tox-tinymce-aux': { zIndex: '1400 !important' },
        }}
      />
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {saveError ? (
          <Alert severity="error" sx={{ m: 1, mb: 0 }} onClose={() => setSaveError(null)}>
            {saveError}
          </Alert>
        ) : null}
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
          <Box
            sx={{
              flex: panelMode === 'right' ? '0 0 0px' : 1,
              minWidth: 0,
              minHeight: 0,
              display: panelMode === 'right' ? 'none' : 'flex',
              flexDirection: 'column',
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={1}
              sx={{ px: 2, py: 0.5 }}
            >
              <Typography variant="subtitle2">Konten custom</Typography>
              <Stack direction="row" gap={1}>
                <Button
                  size="small"
                  startIcon={<RestartAltIcon fontSize="small" />}
                  disabled={!activeEdited || saveMut.isPending}
                  onClick={resetActivePage}
                  sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                  data-testid="lhu-page-reset"
                >
                  Kembalikan ke data asli
                </Button>
                <Button
                  size="small"
                  variant={totalOverrideCount > 0 ? 'contained' : 'outlined'}
                  startIcon={<FormatPaintOutlinedIcon fontSize="small" />}
                  disabled={!previewMounted || saveMut.isPending}
                  onClick={openStyleDialog}
                  sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                  data-testid="lhu-style-override-open"
                >
                  {totalOverrideCount > 0 ? `Override styling (${totalOverrideCount})` : 'Override styling'}
                </Button>
              </Stack>
            </Stack>
            {pages.length > 0 && activeSlot ? (
              <Tabs
                value={activeSlot}
                onChange={(_, value: string) => selectPage(value)}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="Halaman LHU"
                sx={{ minHeight: 40, borderBottom: 1, borderColor: 'divider' }}
              >
                {pages.map((page, index) => (
                  <Tab
                    key={page.slot}
                    value={page.slot}
                    label={editedSlots.has(page.slot) ? `${page.label} · diedit` : page.label}
                    data-testid={`lhu-custom-page-tab-${index}`}
                    sx={{ minHeight: 40, maxWidth: 260, textTransform: 'none', fontSize: 12 }}
                  />
                ))}
              </Tabs>
            ) : null}
            <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 0.75 }}>
              Isi halaman diambil dari data LHU dan bisa diedit bebas. Baris baru dengan formatter membuat baris baru
              dengan format yang sama seperti teks di kursor. Baris polos, Enter di teks berformat, atau klik area kosong
              di bawah konten menaruh kursor di baris baru tanpa format. Shift+Enter memecah baris di tempat kursor.
              Editan hanya berlaku di tampilan LHU ini, data sumber tetap utuh. Halaman yang sudah diedit memakai isi
              editan sampai dikembalikan ke data asli. No. Dok, No. Revisi, dan nomor halaman tetap di paling bawah.
            </Typography>
            <Box sx={{ flex: 1, minHeight: 0, '& .tox-tinymce': { height: '100% !important', border: 0 } }}>
              {htmlQ.isError ? (
                <Box sx={{ p: 2 }}>
                  <Alert severity="error">
                    {htmlQ.error instanceof Error ? htmlQ.error.message : 'Gagal memuat konten LHU.'}
                  </Alert>
                </Box>
              ) : null}
              {loadingEditor || (slotsReady && pages.length === 0 && previewQ.isPending) ? (
                <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', gap: 2 }}>
                  <CircularProgress />
                  <Typography variant="body2" color="text.secondary">
                    Memuat konten…
                  </Typography>
                </Stack>
              ) : null}
              {showEditor && editorSeed ? (
                <Editor
                  key={`${editorSeed.slot}:${editorSeed.nonce}`}
                  licenseKey="gpl"
                  disabled={saveMut.isPending}
                  initialValue={editorSeed.html}
                  onInit={(_evt, editor) => {
                    editorRef.current = editor;
                  }}
                  onDirty={(_evt, editor) => recordEdit(editorSeed.slot, editor.getContent())}
                  onEditorChange={(html, editor) => {
                    if (editor.isDirty()) recordEdit(editorSeed.slot, html);
                  }}
                  init={{
                    height: '100%',
                    menubar: false,
                    toolbar_mode: 'wrap',
                    branding: false,
                    promotion: false,
                    statusbar: false,
                    resize: false,
                    skin: false,
                    content_css: false,
                    content_style: editorContentStyle,
                    body_class: editorSeed.slot === 'cover' ? 'lhu-editor-cover' : 'page-inner',
                    convert_urls: false,
                    automatic_uploads: false,
                    plugins: 'advlist lists table pagebreak',
                    toolbar:
                      'undo redo | lhuPlainLine lhuFormattedLine removeformat | fontfamily fontsize forecolor | bold italic underline | alignleft aligncenter alignright alignjustify | bullist numlist | table pagebreak',
                    setup: bindPlainCaret,
                    font_family_formats:
                      'Arial=arial,helvetica,sans-serif; Times New Roman=times new roman,times,serif; Courier New=courier new,courier,monospace',
                    font_size_formats: '8px 9px 10px 11px 12px 13px 14px 16px 18px 20px 24px 25px 30px',
                    pagebreak_separator:
                      '<div class="page-break" style="page-break-after: always;"></div>',
                    table_default_styles: { 'border-collapse': 'collapse', width: '100%' },
                    table_toolbar:
                      'tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol | tablemergecells tablesplitcells | tablecellprops',
                  }}
                />
              ) : null}
            </Box>
          </Box>
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: { xs: 0.75, md: 0.5 },
              py: { xs: 0.5, md: 0 },
              borderColor: 'divider',
              borderTop: { xs: 1, md: 0 },
              borderBottom: { xs: 1, md: 0 },
              borderLeft: { md: 1 },
              borderRight: { md: 1 },
              bgcolor: 'action.hover',
            }}
          >
            <LhuPanelModeToggle mode={panelMode} vertical={isMdUp} onChange={setPanelMode} />
          </Box>
          <Box
            sx={{
              flex: panelMode === 'left' ? '0 0 0px' : 1,
              minWidth: 0,
              minHeight: 0,
              display: panelMode === 'left' ? 'none' : 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Typography variant="subtitle2" sx={{ px: 2, py: 1 }}>
              Preview LHU
            </Typography>
            <Box sx={{ flex: 1, minHeight: 0, bgcolor: '#d9d9d9', position: 'relative' }}>
              {previewQ.isError ? (
                <Box sx={{ p: 2 }}>
                  <Alert severity="error">
                    {previewQ.error instanceof Error
                      ? previewQ.error.message
                      : 'Gagal memuat preview LHU.'}
                  </Alert>
                </Box>
              ) : null}
              {previewQ.isPending ? (
                <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', gap: 2 }}>
                  <CircularProgress />
                  <Typography variant="body2" color="text.secondary">
                    Menyiapkan preview LHU…
                  </Typography>
                </Stack>
              ) : null}
              <Box
                ref={previewRef}
                data-testid="lhu-custom-html-preview"
                sx={{
                  display: previewSource ? 'block' : 'none',
                  height: '100%',
                  overflow: 'auto',
                  bgcolor: '#d9d9d9',
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ px: 0, pt: 1.5 }}>
        <Button onClick={onClose} disabled={saveMut.isPending}>
          Kembali
        </Button>
        <Button
          variant="contained"
          disabled={!jwt || !slotsReady || saveMut.isPending || htmlQ.isError || pages.length === 0}
          onClick={() => saveMut.mutate({})}
          data-testid="lhu-custom-html-save"
        >
          {saveMut.isPending ? 'Menyimpan…' : 'Simpan'}
        </Button>
      </Stack>
      <Snackbar
        open={saveOk}
        autoHideDuration={4000}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return;
          setSaveOk(false);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        data-testid="lhu-custom-html-save-snackbar"
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSaveOk(false)}
          sx={{ width: '100%' }}
        >
          Konten LHU tersimpan. Footer tetap di paling bawah halaman.
        </Alert>
      </Snackbar>
      {styleDialog ? (
        <LhuStyleOverrideDialog
          open
          initialFormat={styleDialog.initialFormat}
          formats={styleDialog.formats}
          value={styles}
          onApply={applyStyles}
          onClose={() => setStyleDialog(null)}
        />
      ) : null}
    </Box>
  );
}
